import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Virtuoso } from 'react-virtuoso';
import './HomePage.css';
import { sortEvents, interleaveByOrganizer } from '../../utils/eventSorting';
import { useGeolocation, useLocationStore } from '../../stores/locationStore';
import { getCityBySlug } from '../../config/cities.config';
import { SEO } from '../../components/SEO';
import EventCard from '../../components/event/card';
import RecurringEventCard from '../../components/event/RecurringEventCard';
import PreviewCardMap from '../../components/event/PreviewCardMap';
import EventModal from '../../components/event/EventModal';
import { useSmartGeoEvents } from '../../hooks/useSmartGeoEvents';
import { useSeriesBatch } from '../../hooks/useSeriesBatch';
import { Event } from '../../types/event.types';
import { useWouterRouter } from '../../hooks/useWouterNavigation';
import { useModalStack } from '../../hooks/useModalStack';
import MapLibreView from '../../components/map/MapLibreView';
import MapErrorBoundary from '../../components/map/MapErrorBoundary';
import { useEventFilters } from '../../hooks/useEventFilters';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import {
  BottomNav,
  AnimatedList,
  ActiveFiltersBar,
  EventCardSkeleton,
  DateQuickSelectBar
} from '../DesignSystem';
import { useNavigation } from '../../navigation/hooks/useNavigation';
import { BinaryToggle, Icon } from '../DesignSystem';
import LogoSVG from '../DesignSystem/primitives/LogoSVG';

import { useHiddenEvents, useViewMode } from '../../stores/appStore';
import EventModalRoute from '../../components/event/EventModalRoute';
import LocationFallbackToast from '../../components/common/LocationFallbackToast/LocationFallbackToast';
import { locationCache } from '../../services/LocationCacheService';
import { formatDate } from '../../utils/dateUtils';
import { useAnalytics } from '../../contexts/AnalyticsContext';

// Import du test Analytics en développement
if (process.env.NODE_ENV === 'development') {
  import('../../utils/analyticsTestUtils');
  // 🧪 DEBUG IndexedDB
  import('../../utils/debugIndexedDB');
}

// Flag module-level : persiste entre les navigations (mount/unmount)
// Empêche le skeleton de réapparaître quand on revient sur la page
let hasEverLoadedEvents = false;

// 🌍 Track le dernier slug URL→store déjà sync, pour ne recentrer la map QU'À LA PREMIÈRE
// arrivée sur une ville. Sur les remounts suivants (ex: fermeture d'un EventModal ou
// PreviewCardMap depuis /events/hiriketiya/event/xxx → retour à /events/hiriketiya),
// on ne veut PAS resnapper la carte vers la coord ville — l'utilisateur a pané/zoomé
// pendant l'exploration, on doit respecter son viewport.
// Module-level (let) car les useRef se perdent au unmount.
let _lastSyncedCitySlug: string | null = null;

interface GroupedEvents {
  single: Event[];
  recurring: {
    [seriesId: string]: {
      mainEvent: Event;
      events: Event[];
    };
  };
}

// OPTIMISÉ : Interface simplifiée pour la localisation
interface LocationState {
  pendingManual?: { latitude: number; longitude: number }; // Seulement pour les changements en attente
}

// OPTIMISÉ : Interface consolidée pour les filtres
interface FilterState {
  selectedTags: string[];
  searchText: string;
  searchCity: string;
  dateFilter: string[];
  timeFilter: string[];
  customDate: string;
}

// 🎯 PRD HARMONISATION : Constante pour le rayon de chargement geohash
const LOAD_RADIUS = 15; // km - Zone geohash fixe (9 zones de precision 5)

const EmptyStateMessage: React.FC = () => (
  <div className="home__empty-state">
    <h3>No events here... yet!</h3>
    <p>
      Try another location, different tags,<br/>
      or maybe it's time for an ice cream and a massage? 🍦💆‍♂️
    </p>
    <div className="home__empty-state-icon">
      <LogoSVG
        variant="what2Do"
        size="auto"
        padding={8}
        color="var(--primary-dark)"
      />
    </div>
  </div>
);

// Wrapper pour injecter le hook optimisé dans la liste
// 🚀 SMART CACHE - Plus besoin de wrapper optimisé
// Les événements sont déjà chargés via useSmartGeoEvents

const HomePage: React.FC = () => {
  // 🚀 WOUTER: Navigation sans re-renders
  const { navigate, params } = useWouterRouter();
  const { location, loading: geoLoading } = useGeolocation();

  // 🌍 URL-driven location sync: when on /events/:city, the URL slug is the source of truth.
  // Overrides geolocation for SEO city pages, share links, and deep links.
  // (PRD-landing-page-routing — "URL truth-telling")
  const setStoreLocation = useLocationStore(state => state.setLocation);
  useEffect(() => {
    const slug = params?.location;
    if (!slug) return;
    const city = getCityBySlug(slug);
    if (!city) return; // unknown slug → keep current geolocation behavior

    // ⚠️ FIX MAP-SNAP : on ne fait le sync URL→store QU'UNE SEULE FOIS par slug.
    // Sinon, à la fermeture d'un EventModal ou PreviewCardMap (qui ramène l'URL à
    // /events/:slug), l'effect re-fire, recrée un nouvel objet `location` dans le
    // store → EFFET 4 de MapLibreView détecte un changement et re-flyTo vers la
    // coord ville (en zoom DEFAULT_ZOOM) → la carte snap et le user perd son
    // viewport pané/zoomé. En early-return ici, aucun side-effect en aval.
    if (_lastSyncedCitySlug === slug) {
      console.log('🌍 [HomePage] URL→store sync skipped (slug already synced)', slug);
      return;
    }
    console.log('🌍 [HomePage] URL→store sync FIRST visit', slug);
    _lastSyncedCitySlug = slug;
    setStoreLocation(city.latitude, city.longitude, true, 'search');
  }, [params?.location, setStoreLocation]);

  // 🔍 SEO : meta dynamiques selon la ville de l'URL (canonical, title, description, OG).
  // Les bots qui crawlent /events/hiriketiya verront un title et une description spécifiques
  // à Hiriketiya plutôt qu'un fallback générique.
  const seoCity = useMemo(() => {
    const slug = params?.location;
    return slug ? getCityBySlug(slug) : undefined;
  }, [params?.location]);

  // 🔄 PHASE 4 PRD Navigation : Hook centralisé pour la stack de modales
  const {
    isPreviewCardMapOpen,
    isEventModalOpen,
    previewCardMapData,
    eventModalData,
    openPreviewCardMap,
    openEventModal,
    closeTopModal,
    closeAll
  } = useModalStack();

  // ✅ SIMPLIFIÉ : Plus d'état local redondant, juste les changements en attente
  const [locationState, setLocationState] = useState<LocationState>({});

  const [filterState, setFilterState] = useState<FilterState>({
    selectedTags: [],
    searchText: '',
    searchCity: '',
    dateFilter: [],
    timeFilter: [],
    customDate: ''
  });

  // 📅 Force open filter drawer from quick-select bar
  const [forceOpenFilter, setForceOpenFilter] = useState(false);

  // 📅 Date range state
  const [dateRangeStart, setDateRangeStart] = useState<Date | null>(null);
  const [dateRangeEnd, setDateRangeEnd] = useState<Date | null>(null);

  // 📐 Measure ActiveFiltersBar height → CSS variable for DatePicker map positioning
  const activeFiltersRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!activeFiltersRef.current) return;
    const updateHeight = () => {
      const h = activeFiltersRef.current?.offsetHeight || 0;
      document.documentElement.style.setProperty('--active-filters-height', `${h > 0 ? h + 4 : 0}px`);
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(activeFiltersRef.current);
    return () => observer.disconnect();
  }, [filterState.selectedTags, filterState.timeFilter, filterState.searchText, filterState.searchCity]);

  // OPTIMISÉ : États de vue et UI - Zustand store partagé avec TouristDesktopLayout
  const { viewMode, setViewMode } = useViewMode();
  const analytics = useAnalytics();
  const previousViewModeRef = useRef(viewMode);

  // Desktop detection - pour ne pas rendre le toggle mobile sur desktop
  const isDesktop = useIsDesktop();

  // ✅ NOUVEAU : Utilisation du hook pour les events masqués
  const { isHidden, undoLastRemoval, hiddenEventsDetails } = useHiddenEvents();

  // ✅ SUPPRIMÉ: HomePage n'a pas besoin de location.state - c'est géré par EventModalRoute

  // ✅ PERF FIX: Ref pour garder une référence stable de effectiveLocation
  const effectiveLocationRef = useRef<{ latitude: number; longitude: number } | undefined>(undefined);

  // OPTIMISÉ : Calcul de la localisation effective - suit directement useGeolocation
  const effectiveLocation = useMemo(() => {
    let newLocation: { latitude: number; longitude: number } | undefined;

    // 1. Priorité aux changements en attente (pas encore sauvegardés)
    if (locationState.pendingManual) {
      newLocation = locationState.pendingManual;
    }
    // 2. Sinon, suivre useGeolocation (qui gère déjà les priorités)
    else if (location) {
      newLocation = {
        latitude: location.latitude,
        longitude: location.longitude
      };
    }

    // ✅ PERF: Garder la même référence si les valeurs n'ont pas changé
    if (
      effectiveLocationRef.current?.latitude === newLocation?.latitude &&
      effectiveLocationRef.current?.longitude === newLocation?.longitude
    ) {
      return effectiveLocationRef.current;
    }

    // Nouvelle location → mettre à jour la ref
    effectiveLocationRef.current = newLocation;
    return newLocation;
  }, [locationState.pendingManual, location]);

  // 🎯 PRD HARMONISATION : maxDistance contrôlé par slider (défaut 5km, max 15km)
  const [maxDistance, setMaxDistance] = useState(5);

  // 🚀 SMART CACHE - Rayon de chargement FIXE à 15km (zone geohash)
  const {
    events,
    loading: eventsLoading
  } = useSmartGeoEvents(effectiveLocation, LOAD_RADIUS);

  // Marquer quand les events sont chargés (persiste via flag module-level)
  if (events.length > 0) hasEverLoadedEvents = true;

  // 🐛 DEBUG : Tracer les événements (résumé)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && events.length > 0) {
      console.log(`🎯 [HomePage] Events reçus de useSmartGeoEvents: ${events.length}`);
    }
  }, [events]);

  // 🏠 Dispatcher un événement quand la page est prête (events chargés + map visible)
  useEffect(() => {
    if (!eventsLoading && events.length > 0 && !geoLoading) {
      // Attendre un peu pour s'assurer que la map est rendue
      const timer = setTimeout(() => {
        window.dispatchEvent(new CustomEvent('homePageReady'));
      }, 500); // Délai pour laisser la map se rendre

      return () => clearTimeout(timer);
    }
  }, [eventsLoading, events.length, geoLoading]);

  // OPTIMISÉ : Gestion de la localisation consolidée
  // ✅ SUPPRIMÉ : L'effet qui créait des conflits
  // useEffect(() => {
  //   if (location && !location.error) {
  //     setLocationState(prev => ({
  //       ...prev,
  //       current: {
  //         latitude: location.latitude,
  //         longitude: location.longitude
  //       },
  //       effective: prev.manual || {
  //         latitude: location.latitude,
  //         longitude: location.longitude
  //       }
  //     }));
  //   }
  // }, [location]);

  // OPTIMISÉ : Gestion du mode de vue - via store Zustand partagé
  const handleViewModeChange = useCallback(() => {
    setViewMode(viewMode === 'list' ? 'map' : 'list');
  }, [viewMode, setViewMode]);

  // 🗺️ Fallback carte (WebGL indisponible / crash MapLibre) : bascule vers le
  // toggle vue liste déjà existant plutôt que de réinventer une navigation.
  const handleSwitchToList = useCallback(() => {
    setViewMode('list');
  }, [setViewMode]);

  // ✅ Fermer PreviewCardMap automatiquement quand on passe en vue liste
  // 🔧 FIX PHASE 4: Utiliser closeAll() au lieu de navigate() pour synchroniser stack ET URL
  useEffect(() => {
    if (viewMode === 'list' && isPreviewCardMapOpen) {
      closeAll();
    }
  }, [viewMode, isPreviewCardMapOpen, closeAll]);

  // 🖥️ Desktop: Broadcast filter state to TouristDesktopLayout
  const broadcastFilterState = useCallback(() => {
    window.dispatchEvent(new CustomEvent('filterStateUpdate', {
      detail: {
        selectedTags: filterState.selectedTags,
        searchText: filterState.searchText,
        searchCity: filterState.searchCity,
        dateFilter: filterState.dateFilter,
        timeFilter: filterState.timeFilter,
        distance: maxDistance,
        currentLocation: effectiveLocation
      }
    }));
  }, [filterState, maxDistance, effectiveLocation]);

  // 🖥️ Desktop: Listen for filter changes from TouristDesktopLayout
  useEffect(() => {
    const handleFilterChange = (event: CustomEvent<{ type: string; value: unknown }>) => {
      const { type, value } = event.detail;

      switch (type) {
        case 'searchText':
          setFilterState(prev => ({ ...prev, searchText: value as string }));
          break;
        case 'searchCity':
          setFilterState(prev => ({ ...prev, searchCity: value as string }));
          break;
        case 'location': {
          const loc = value as { latitude: number; longitude: number; shouldCenter?: boolean };
          setLocationState({ pendingManual: loc });
          const locationData = {
            latitude: loc.latitude,
            longitude: loc.longitude,
            timestamp: Date.now(),
            source: 'manual' as const
          };
          localStorage.setItem('userLocation', JSON.stringify(locationData));
          window.dispatchEvent(new CustomEvent('locationUpdate', { detail: locationData }));
          setTimeout(() => setLocationState({ pendingManual: undefined }), 100);
          break;
        }
        case 'distance':
          setMaxDistance(value as number);
          break;
        case 'dateFilter':
          setFilterState(prev => ({ ...prev, dateFilter: value as string[] }));
          break;
        case 'timeFilter':
          setFilterState(prev => ({ ...prev, timeFilter: value as string[] }));
          break;
        case 'tagSelect': {
          const tagId = value as string;
          setFilterState(prev => ({
            ...prev,
            selectedTags: prev.selectedTags.includes(tagId)
              ? prev.selectedTags.filter(id => id !== tagId)
              : [...prev.selectedTags, tagId]
          }));
          break;
        }
        case 'reset':
          setFilterState({
            selectedTags: [],
            searchText: '',
            searchCity: '',
            dateFilter: [],
            timeFilter: [],
            customDate: ''
          });
          setDateRangeStart(null);
          setDateRangeEnd(null);
          setMaxDistance(5);
          break;
      }
    };

    const handleRequestFilterState = () => {
      broadcastFilterState();
    };

    window.addEventListener('filterChange', handleFilterChange as EventListener);
    window.addEventListener('requestFilterState', handleRequestFilterState);

    return () => {
      window.removeEventListener('filterChange', handleFilterChange as EventListener);
      window.removeEventListener('requestFilterState', handleRequestFilterState);
    };
  }, [broadcastFilterState]);

  // 🖥️ Desktop: Broadcast filter state when it changes
  useEffect(() => {
    broadcastFilterState();
  }, [broadcastFilterState]);

  // OPTIMISÉ : Gestion du drawer
  // 🔧 FIX: navigate vers /events/:city (URL parente) au lieu de '/' qui = LandingPage maintenant
  const handleDrawerOpen = useCallback(() => {
    if (window.location.pathname.includes('/events/') || window.location.pathname.includes('/event/')) {
      // Extract current city from URL or fallback to default
      const cityMatch = window.location.pathname.match(/\/events\/([^/]+)/);
      const targetUrl = cityMatch ? `/events/${cityMatch[1]}` : '/events/hiriketiya';
      navigate(targetUrl, { replace: true });
      return new Promise<void>(resolve => {
        setTimeout(resolve, 50);
      });
    }
    return Promise.resolve();
  }, [navigate]); // ✅ Seulement navigate nécessaire

  // OPTIMISÉ : Groupement des événements avec useMemo
  const groupedEvents = useMemo(() => {
    const grouped: GroupedEvents = {
      single: [],
      recurring: {}
    };

    if (!events) return grouped;

    events.forEach(event => {
      if (event.seriesId) {
        if (!grouped.recurring[event.seriesId]) {
          grouped.recurring[event.seriesId] = {
            mainEvent: event,
            events: []
          };
        }
        grouped.recurring[event.seriesId].events.push(event);
      } else {
        grouped.single.push(event);
      }
    });

    return grouped;
  }, [events]);

  // 🔄 PHASE 4 PRD Navigation : Gestionnaire d'événements utilisant useModalStack
  const handleEventClick = useCallback((event: Event, seriesEvents?: Event[]) => {
    openEventModal(event, {
      seriesEvents,
      fromPreviewCardMap: false,
      isMapView: viewMode === 'map',
      userLocation: effectiveLocation ? { lat: effectiveLocation.latitude, lng: effectiveLocation.longitude } : null
    });
  }, [openEventModal, viewMode, effectiveLocation]);

  // 🔧 FIX: navigate vers /events/:city au lieu de '/' qui = LandingPage maintenant
  const handleMultiModalClose = useCallback(() => {
    const cityMatch = window.location.pathname.match(/\/events\/([^/]+)/);
    const targetUrl = cityMatch ? `/events/${cityMatch[1]}` : '/events/hiriketiya';
    navigate(targetUrl, { replace: true });
  }, [navigate]);

  // ✅ SUPPRIMÉ: handleMultiEventClick inutilisée - logique gérée par EventModalRoute



  // OPTIMISÉ : Gestionnaires de filtres avec useCallback
  const handleTimeFilterChange = useCallback((times: string[]) => {
    setFilterState(prev => ({ ...prev, timeFilter: times }));
  }, []);

  const handleTagSelect = useCallback((tagId: string) => {
    setFilterState(prev => ({
      ...prev,
      selectedTags: prev.selectedTags.includes(tagId)
        ? prev.selectedTags.filter(id => id !== tagId)
        : [...prev.selectedTags, tagId]
    }));
  }, []);

  // 📅 handleDateFilterChange: called by FilterDrawer (single source of truth)
  const handleDateFilterChange = useCallback((dates: string[]) => {
    setFilterState(prev => ({ ...prev, dateFilter: dates }));
  }, []);

  // 📅 Resolve dateFilter → concrete YYYY-MM-DD dates for pill display
  const resolvedPillDates = useMemo((): string[] => {
    const resolved: string[] = [];
    const today = new Date();
    for (const filter of filterState.dateFilter) {
      if (filter.startsWith('custom_') || filter.startsWith('day_')) {
        resolved.push(filter.substring(filter.indexOf('_') + 1));
      } else if (filter === 'today') {
        resolved.push(formatDate(today));
      } else if (filter === 'tomorrow') {
        const tmr = new Date(today);
        tmr.setDate(tmr.getDate() + 1);
        resolved.push(formatDate(tmr));
      } else if (filter === 'weekend' || filter === 'this_weekend') {
        // Samedi + Dimanche uniquement
        const d = new Date(today);
        while (d.getDay() !== 6) d.setDate(d.getDate() + 1); // next Saturday
        resolved.push(formatDate(new Date(d)));
        const sun = new Date(d);
        sun.setDate(d.getDate() + 1);
        resolved.push(formatDate(sun));
      }
    }
    return resolved;
  }, [filterState.dateFilter]);

  // 📅 Set of YYYY-MM-DD covered by the 7-day pill bar (for tag filtering)
  const pillDateSet = useMemo(() => {
    const set = new Set<string>();
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      set.add(formatDate(d));
    }
    return set;
  }, []);

  // 📅 Pill toggle → update filterState.dateFilter with smart reverse mapping
  const handlePillToggle = useCallback((dateStr: string) => {
    setFilterState(prev => {
      const today = new Date();
      const todayStr = formatDate(today);
      const tmr = new Date(today);
      tmr.setDate(tmr.getDate() + 1);
      const tomorrowStr = formatDate(tmr);

      // Resolve current dateFilter to see if this date is already selected
      const currentResolved: string[] = [];
      for (const f of prev.dateFilter) {
        if (f.startsWith('custom_') || f.startsWith('day_')) {
          currentResolved.push(f.substring(f.indexOf('_') + 1));
        } else if (f === 'today') {
          currentResolved.push(todayStr);
        } else if (f === 'tomorrow') {
          currentResolved.push(tomorrowStr);
        } else if (f === 'weekend' || f === 'this_weekend') {
          const d = new Date(today);
          while (d.getDay() !== 6) d.setDate(d.getDate() + 1);
          currentResolved.push(formatDate(new Date(d)));
          const sun = new Date(d);
          sun.setDate(d.getDate() + 1);
          currentResolved.push(formatDate(sun));
        }
      }

      const isCurrentlySelected = currentResolved.includes(dateStr);

      if (isCurrentlySelected) {
        // DESELECT: remove filters that resolve to this date
        let newFilters = prev.dateFilter.filter(f => {
          if (f.startsWith('custom_') || f.startsWith('day_')) return f.substring(f.indexOf('_') + 1) !== dateStr;
          if (f === 'today') return todayStr !== dateStr;
          if (f === 'tomorrow') return tomorrowStr !== dateStr;
          // Weekend: keep it only if neither Sat nor Sun matches
          if (f === 'weekend' || f === 'this_weekend') {
            const d = new Date(today);
            while (d.getDay() !== 6) d.setDate(d.getDate() + 1);
            const satStr = formatDate(new Date(d));
            const sun = new Date(d);
            sun.setDate(d.getDate() + 1);
            const sunStr = formatDate(sun);
            if (satStr === dateStr || sunStr === dateStr) {
              // Decompose: keep the other weekend day as custom
              const otherDay = satStr === dateStr ? sunStr : satStr;
              newFilters.push(`day_${otherDay}`);
              return false; // remove "weekend"
            }
            return true;
          }
          return true;
        });
        return { ...prev, dateFilter: newFilters };
      } else {
        // SELECT: add semantic filter if possible, else custom
        let newFilter: string;
        if (dateStr === todayStr) {
          newFilter = 'today';
        } else if (dateStr === tomorrowStr) {
          newFilter = 'tomorrow';
        } else {
          newFilter = `day_${dateStr}`;
        }
        return { ...prev, dateFilter: [...prev.dateFilter, newFilter] };
      }
    });
  }, []);

  const handleOpenFiltersFromQuickSelect = useCallback(() => {
    setForceOpenFilter(true);
  }, []);

  // Listen for openLocationFilter event from LocationFallbackToast
  useEffect(() => {
    const handler = () => setForceOpenFilter(true);
    window.addEventListener('openLocationFilter', handler);
    return () => window.removeEventListener('openLocationFilter', handler);
  }, []);

  const handleDateRangeChange = useCallback((start: Date | null, end: Date | null) => {
    setDateRangeStart(start);
    setDateRangeEnd(end);
  }, []);

  // Gestion des filtres consolidée
  const filteredEvents = useEventFilters(events, {
    searchText: filterState.searchText,
    searchCity: filterState.searchCity,
    selectedTags: filterState.selectedTags,
    dateFilter: filterState.dateFilter,
    timeFilter: filterState.timeFilter,
    dateRangeStart,
    dateRangeEnd,
    distance: maxDistance,
    customDate: filterState.customDate,
    userLocation: effectiveLocation,
  });

  // 🐛 DEBUG : Tracer filteredEvents (résumé)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`🎯 [HomePage] FilteredEvents après filtres: ${filteredEvents.length}`);
    }
  }, [filteredEvents]);

  // Événements visibles dans la liste - AnimatedList gère maintenant les animations
  const listVisibleEvents = useMemo(() => {
    // ✅ OPTIMISATION : Vérifier si il y a des événements cachés
    const hasHiddenEvents = filteredEvents.some(event => isHidden(event.id, event.seriesId));

    if (!hasHiddenEvents) {
      return filteredEvents; // ✅ Retourner référence originale si pas d'événements cachés
    }

    const visible = filteredEvents.filter(event => !isHidden(event.id, event.seriesId));
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔍 [HomePage] hiddenEvents: ${visible.length}/${filteredEvents.length} visibles (${filteredEvents.length - visible.length} cachés)`);
    }
    return visible;
  }, [filteredEvents, isHidden]);

  const mapVisibleEvents = filteredEvents; // ✅ Carte : tous les événements, même les "cachés"

  // ===== ANALYTICS: Track search usage =====
  useEffect(() => {
    if (filterState.searchText.trim().length > 0) {
      analytics.trackSearch(filterState.searchText, filteredEvents.length);
    }
  }, [filterState.searchText, filteredEvents.length, analytics]);

  // ===== ANALYTICS: Track filter usage =====
  useEffect(() => {
    const hasActiveFilters =
      filterState.selectedTags.length > 0 ||
      filterState.dateFilter.length > 0 ||
      filterState.timeFilter.length > 0 ||
      filterState.searchCity.trim().length > 0 ||
      maxDistance !== 5;

    if (hasActiveFilters) {
      const filters = {
        tags: filterState.selectedTags.length > 0 ? filterState.selectedTags : undefined,
        date: filterState.dateFilter.length > 0 ? filterState.dateFilter.join(',') : undefined,
        time: filterState.timeFilter.length > 0 ? filterState.timeFilter.join(',') : undefined,
        city: filterState.searchCity.trim().length > 0 ? filterState.searchCity : undefined,
        distance: maxDistance !== 5 ? maxDistance : undefined,
      };

      analytics.trackFilters(filters, filteredEvents.length);
      analytics.markFilterApplied();
    }
  }, [
    filterState.selectedTags,
    filterState.dateFilter,
    filterState.timeFilter,
    filterState.searchCity,
    maxDistance,
    filteredEvents.length,
    analytics
  ]);

  // ===== ANALYTICS: Track view mode change (liste ↔ carte) =====
  useEffect(() => {
    if (previousViewModeRef.current !== viewMode) {
      analytics.trackViewModeChange(viewMode, previousViewModeRef.current);
      previousViewModeRef.current = viewMode;
    }
  }, [viewMode, analytics]);

  // OPTIMISÉ : Fonction de groupement avec useCallback et cache intelligent
  const groupRecurringEvents = useCallback((events: Event[]) => {
    const hasRecurringEvents = events.some(event => event.seriesId);
    if (!hasRecurringEvents) return events;

    const grouped: Event[] = [];
    const seenSeriesIds = new Set<string>();

    events.forEach(event => {
      if (event.seriesId) {
        if (!seenSeriesIds.has(event.seriesId)) {
          seenSeriesIds.add(event.seriesId);
          grouped.push(event);
        }
      } else {
        grouped.push(event);
      }
    });

    return grouped;
  }, []);

  // OPTIMISÉ : Événements pour la carte avec useMemo (utilise mapVisibleEvents)
  const mapEvents = useMemo(() => {
    return groupRecurringEvents(mapVisibleEvents);
  }, [mapVisibleEvents, groupRecurringEvents]);

  // OPTIMISÉ : Événements triés avec useMemo (utilise listVisibleEvents pour la liste)
  // 🎯 PRD HARMONISATION : maxDistance connecté au slider
  const sortedEvents = useMemo(() => {
    // Déterminer si un filtre date est actif
    const hasDateFilter = filterState.dateFilter.length > 0 || !!dateRangeStart || !!dateRangeEnd;

    return sortEvents(
      groupRecurringEvents(listVisibleEvents),
      viewMode,
      effectiveLocation,
      maxDistance,
      hasDateFilter
    );
  }, [listVisibleEvents, groupRecurringEvents, viewMode, effectiveLocation, maxDistance, filterState.dateFilter, dateRangeStart, dateRangeEnd]);

  // OPTIMISÉ : Événements pour la liste avec useMemo
  const listEvents = useMemo(() => {
    return sortedEvents.map(event => {
      if (event.seriesId && groupedEvents.recurring[event.seriesId]) {
        return {
          mainEvent: event,
          seriesEvents: groupedEvents.recurring[event.seriesId].events
        };
      }
      return { mainEvent: event, seriesEvents: [] };
    });
  }, [sortedEvents, groupedEvents]);

  // 🚀 BATCH LOADING des séries - 1 seul chargement au lieu de 24 useSmartSeries individuels
  const recurringSeriesIds = useMemo(() =>
    listEvents
      .filter(({ mainEvent }) => mainEvent.seriesId && groupedEvents.recurring[mainEvent.seriesId])
      .map(({ mainEvent }) => mainEvent.seriesId!)
  , [listEvents, groupedEvents]);

  // Map des occurrences par seriesId (pour fallback endDate quand série sans config de fin)
  const seriesEventsMap = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const seriesId of Object.keys(groupedEvents.recurring)) {
      map.set(seriesId, groupedEvents.recurring[seriesId].events);
    }
    return map;
  }, [groupedEvents]);

  const seriesPatternsMap = useSeriesBatch(recurringSeriesIds, seriesEventsMap);

  // 🐛 DEBUG : Comparer liste vs map (résumé compact)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && events.length > 0) {
      console.log(`📊 [HomePage] Events: ${events.length} total → Liste: ${sortedEvents.length} | Map: ${mapEvents.length}`);
    }
  }, [events.length, sortedEvents.length, mapEvents.length]);

  // ✅ PLACÉ ICI : Navigation avec événements groupés (après toutes les déclarations)
  const handleMapEventClick = useCallback(async (event: Event, clusterEvents?: Event[]) => {
    // Vérifier que l'événement a des coordonnées valides
    if (!event.latitude || !event.longitude) return;

    // ✅ Utiliser directement clusterEvents si fourni (vient du clustering MapLibre)
    // Sinon fallback sur le filtrage par position (compatibilité)
    const eventsAtSameLocation = clusterEvents || mapEvents.filter(e =>
      e.location === event.location &&
      e.latitude && e.longitude &&
      Math.abs(e.latitude - event.latitude!) < 0.001 &&
      Math.abs(e.longitude - event.longitude!) < 0.001
    );

    // 🚀 OPTIMISÉ : Enrichir avec les séries complètes + signature stable
    const enrichedEvents = await Promise.all(
      eventsAtSameLocation.map(async (eventRepresentant) => {
        if (!eventRepresentant.seriesId) {
          // Événement simple, retourner tel quel avec signature stable
          return { 
            main: { 
              id: eventRepresentant.id, 
              seriesId: eventRepresentant.seriesId,
              title: eventRepresentant.title,
              datetime: eventRepresentant.datetime,
              location: eventRepresentant.location,
              latitude: eventRepresentant.latitude,
              longitude: eventRepresentant.longitude
            }, 
            series: [],
            _signature: `${eventRepresentant.id}:0` // Signature stable pour React.memo
          };
        }
        
        // Événement de série, récupérer toutes les occurrences
        try {
          // 1. Essayer depuis SmartCache
          const seriesEvents = await locationCache.getSeriesEvents(eventRepresentant.seriesId);
          
          if (seriesEvents && seriesEvents.length > 0) {
            return {
              main: { 
                id: eventRepresentant.id, 
                seriesId: eventRepresentant.seriesId,
                title: eventRepresentant.title,
                datetime: eventRepresentant.datetime,
                location: eventRepresentant.location,
                latitude: eventRepresentant.latitude,
                longitude: eventRepresentant.longitude
              },
              series: seriesEvents.map(e => ({ 
                id: e.id, 
                datetime: e.datetime,
                title: e.title 
              })),
              _signature: `${eventRepresentant.id}:${seriesEvents.length}` // Signature stable
            };
          }
          
          // 2. Fallback : chercher dans groupedEvents local
          const localSeries = groupedEvents.recurring[eventRepresentant.seriesId];
          if (localSeries) {
            return {
              main: { 
                id: eventRepresentant.id, 
                seriesId: eventRepresentant.seriesId,
                title: eventRepresentant.title,
                datetime: eventRepresentant.datetime,
                location: eventRepresentant.location,
                latitude: eventRepresentant.latitude,
                longitude: eventRepresentant.longitude
              },
              series: localSeries.events.map(e => ({ 
                id: e.id, 
                datetime: e.datetime,
                title: e.title 
              })),
              _signature: `${eventRepresentant.id}:${localSeries.events.length}` // Signature stable
            };
          }
          
          return {
            main: { 
              id: eventRepresentant.id, 
              seriesId: eventRepresentant.seriesId,
              title: eventRepresentant.title,
              datetime: eventRepresentant.datetime,
              location: eventRepresentant.location,
              latitude: eventRepresentant.latitude,
              longitude: eventRepresentant.longitude
            }, 
            series: [],
            _signature: `${eventRepresentant.id}:0` // Signature stable
          };
          
        } catch (error) {
          console.error('❌ [HomePage] Erreur enrichissement série:', error);
          return { 
            main: { 
              id: eventRepresentant.id, 
              seriesId: eventRepresentant.seriesId,
              title: eventRepresentant.title,
              datetime: eventRepresentant.datetime,
              location: eventRepresentant.location,
              latitude: eventRepresentant.latitude,
              longitude: eventRepresentant.longitude
            }, 
            series: [],
            _signature: `${eventRepresentant.id}:0` // Signature stable
          };
        }
      })
    );
    
    // 🔄 PHASE 4 PRD Navigation : Utiliser openPreviewCardMap

    // Tri par date (plus proche en premier) + diversité d'organisateurs
    const sortedByDate = [...eventsAtSameLocation].sort((a, b) => {
      const dateA = a.datetime ? new Date(a.datetime).getTime() : Infinity;
      const dateB = b.datetime ? new Date(b.datetime).getTime() : Infinity;
      return dateA - dateB;
    });
    const diversifiedEvents = interleaveByOrganizer(sortedByDate);

    openPreviewCardMap(
      {
        events: diversifiedEvents,
        enrichedEvents: enrichedEvents,
        location: event.location || '',
        userLocation: effectiveLocation ? { lat: effectiveLocation.latitude, lng: effectiveLocation.longitude } : null
      },
      event.location || ''
    );
  }, [openPreviewCardMap, effectiveLocation, mapEvents, groupedEvents]);

  // 🔧 PHASE 4 PRD Navigation : Handler popstate supprimé
  // La gestion du bouton retour est maintenant centralisée dans useModalStack
  // L'ancien code ajoutait une entrée '/' à l'historique et faisait des history.go(-1)
  // supplémentaires qui corrompaient la navigation modale

  // OPTIMISÉ : Navigation avec useMemo
  const { getNavigationItems } = useNavigation('public');
  const navigationItems = useMemo(() => 
    getNavigationItems({
      viewMode,
      favoritesCount: 0, // ✅ SIMPLIFIÉ : Plus de gestion locale des favoris
      onViewModeChange: handleViewModeChange
    }), [getNavigationItems, viewMode, handleViewModeChange]);

  // 🚀 PHASE 3 PRD PERF : Exit/Enter animations pour Virtuoso mobile
  // Utilise un event listener synchrone sur 'hiddenEventsUpdated' (dispatché par addToHiddenEvents)
  // pour que exitingIds soit mis à jour dans le MÊME batch React que hiddenEvents.
  // Cela évite le flash où la carte disparaît puis réapparaît.
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());
  const [enteringIds, setEnteringIds] = useState<Set<string>>(new Set());
  const exitTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    if (isDesktop) return;

    const handler: EventListener = (e) => {
      const detail = (e as unknown as CustomEvent<{ action: string; eventId: string }>).detail;
      if (!detail?.eventId) return;

      if (detail.action === 'add') {
        // Event caché (swipe/favorite) → animation de sortie
        setExitingIds(prev => {
          const next = new Set(Array.from(prev));
          next.add(detail.eventId);
          return next;
        });
        const timeout = setTimeout(() => {
          setExitingIds(prev => {
            const next = new Set(prev);
            next.delete(detail.eventId);
            return next;
          });
          exitTimeoutsRef.current.delete(detail.eventId);
        }, 280);
        exitTimeoutsRef.current.set(detail.eventId, timeout);
      }

      if (detail.action === 'remove') {
        // Event restauré (undo) → annuler exit si en cours, lancer animation d'entrée
        const existingTimeout = exitTimeoutsRef.current.get(detail.eventId);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
          exitTimeoutsRef.current.delete(detail.eventId);
          setExitingIds(prev => {
            const next = new Set(prev);
            next.delete(detail.eventId);
            return next;
          });
        }
        setEnteringIds(prev => {
          const next = new Set(Array.from(prev));
          next.add(detail.eventId);
          return next;
        });
        setTimeout(() => {
          setEnteringIds(prev => {
            const next = new Set(prev);
            next.delete(detail.eventId);
            return next;
          });
        }, 350);
      }
    };

    window.addEventListener('hiddenEventsUpdated' as string, handler);
    return () => {
      window.removeEventListener('hiddenEventsUpdated' as string, handler);
      exitTimeoutsRef.current.forEach(t => clearTimeout(t));
    };
  }, [isDesktop]);

  // Liste visible = non-hidden + en cours d'exit (pour l'animation)
  // IMPORTANT: PAS de hiddenEventsDetails dans les deps !
  // Zustand/useSyncExternalStore force un render synchrone quand hiddenEvents change,
  // AVANT que l'event listener puisse mettre à jour exitingIds.
  // Sans hiddenEventsDetails dans les deps, le memo retourne la valeur cached (carte encore là)
  // puis exitingIds change → deuxième render avec la classe --exiting.
  // enteringIds est nécessaire pour déclencher le recalcul lors d'un undo.
  const visibleListEvents = useMemo(() => {
    return listEvents.filter(({ mainEvent }) => {
      const eventId = mainEvent.seriesId || mainEvent.id;
      return !isHidden(eventId) || exitingIds.has(eventId);
    });
  }, [listEvents, isHidden, exitingIds, enteringIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // OPTIMISÉ : Rendu des événements — Virtuoso sur mobile, AnimatedList sur desktop
  const renderEvents = useCallback(() => {
    // 📱 Mobile : react-virtuoso (ne rend que ~8 cartes visibles)
    if (!isDesktop) {
      return (
        <Virtuoso
          className="virtuoso-event-list"
          style={{ height: '100%' }}
          data={visibleListEvents}
          overscan={800}
          computeItemKey={(index, item) => item.mainEvent.seriesId || item.mainEvent.id}
          components={{
            Header: () => <div style={{ height: 78 }} aria-hidden="true" />,
            Footer: () => <div className="list-bottom-spacer" aria-hidden="true" />
          }}
          itemContent={(index, { mainEvent, seriesEvents }) => {
            const eventId = mainEvent.seriesId || mainEvent.id;
            const isExiting = exitingIds.has(eventId);
            const isEntering = enteringIds.has(eventId);
            const wrapperClass = `virtuoso-item-wrapper${isExiting ? ' virtuoso-item-wrapper--exiting' : ''}${isEntering ? ' virtuoso-item-wrapper--entering' : ''}`;
            if (mainEvent.seriesId && groupedEvents.recurring[mainEvent.seriesId]) {
              return (
                <div className={wrapperClass}>
                  <RecurringEventCard
                    eventId={mainEvent.id}
                    mainEvent={mainEvent}
                    seriesEvents={seriesEvents}
                    userLocation={effectiveLocation}
                    isMapView={viewMode === 'map'}
                    isFirstCard={index === 0}
                    onClick={handleEventClick}
                    seriesPattern={seriesPatternsMap.get(mainEvent.seriesId!)?.pattern ?? null}
                    seriesDaySchedules={seriesPatternsMap.get(mainEvent.seriesId!)?.daySchedules}
                  />
                </div>
              );
            } else {
              return (
                <div className={wrapperClass}>
                  <EventCard
                    event={mainEvent}
                    seriesEvents={mainEvent.seriesId ? seriesEvents : undefined}
                    userLocation={effectiveLocation}
                    isFirstCard={index === 0}
                    isVisible={viewMode === 'list'}
                    onClick={handleEventClick}
                  />
                </div>
              );
            }
          }}
        />
      );
    }

    // 🖥️ Desktop : AnimatedList avec grid 2 colonnes
    return (
      <AnimatedList>
        {listEvents.map(({ mainEvent, seriesEvents }, index) => {
          if (mainEvent.seriesId && groupedEvents.recurring[mainEvent.seriesId]) {
            return (
              <RecurringEventCard
                key={mainEvent.seriesId}
                eventId={mainEvent.id}
                mainEvent={mainEvent}
                seriesEvents={seriesEvents}
                userLocation={effectiveLocation}
                isMapView={viewMode === 'map'}
                isFirstCard={index === 0}
                onClick={handleEventClick}
                seriesPattern={seriesPatternsMap.get(mainEvent.seriesId!)?.pattern ?? null}
              />
            );
          } else {
            return (
              <EventCard
                key={mainEvent.id}
                event={mainEvent}
                seriesEvents={mainEvent.seriesId ? seriesEvents : undefined}
                userLocation={effectiveLocation}
                isFirstCard={index === 0}
                isVisible={viewMode === 'list'}
                onClick={handleEventClick}
              />
            );
          }
        })}
      </AnimatedList>
    );
  }, [listEvents, visibleListEvents, groupedEvents, effectiveLocation, viewMode, handleEventClick, isDesktop, resolvedPillDates, handlePillToggle, handleOpenFiltersFromQuickSelect, seriesPatternsMap, isHidden, exitingIds, enteringIds]);

  // ✅ SIMPLIFIÉ : Gestionnaire de localisation qui suit la logique de priorité
  const handleCurrentLocationChange = useCallback((location: { 
    latitude: number; 
    longitude: number;
    shouldCenter?: boolean;
  }) => {
    // ✅ NOUVEAU : Toujours marquer comme changement en attente pour synchroniser
    setLocationState({ pendingManual: location });
    
    // ✅ NOUVEAU : Sauvegarder même si shouldCenter = false pour synchroniser les filtres
    const locationData = {
      latitude: location.latitude,
      longitude: location.longitude,
      timestamp: Date.now(),
      source: 'manual' as const
    };
    
    // Sauvegarder et déclencher la mise à jour globale
    localStorage.setItem('userLocation', JSON.stringify(locationData));
    
    window.dispatchEvent(new CustomEvent('locationUpdate', {
      detail: locationData
    }));
    
    // ✅ NOUVEAU : Nettoyer l'état en attente une fois sauvegardé
    setTimeout(() => {
      setLocationState({ pendingManual: undefined });
    }, 100);
  }, []);

  return (
    <div className="home">
      {/* SEO: dynamic meta tags per city. Falls back to generic Sri Lanka meta if the
          slug doesn't match any city (defensive; URL slug validation happens upstream). */}
      <SEO
        title={
          seoCity
            ? `Events in ${seoCity.label}, ${seoCity.country}`
            : 'Discover Local Events in Sri Lanka'
        }
        description={
          seoCity
            ? `${seoCity.description}. Discover what's happening in ${seoCity.label} — free for tourists, no app needed.`
            : undefined /* SEO component falls back to DEFAULT_DESCRIPTION */
        }
        url={seoCity ? `/events/${seoCity.slug}` : '/events'}
        type="website"
      />
      {/* Visually hidden H1 — kept for accessibility and bots that don't render JS-rendered title (PRD Performance Phase 1) */}
      <h1 className="visually-hidden">
        {seoCity
          ? `Events in ${seoCity.label}, ${seoCity.country} — concerts, festivals, activities`
          : 'What 2 Do ? - Discover events, concerts, festivals and activities in Sri Lanka'}
      </h1>
      <div className="home__content">
        {/* Desktop: ActiveFiltersBar at top (absolute positioning over map) */}
        {isDesktop && (
          <ActiveFiltersBar
            selectedTags={filterState.selectedTags}
            onTagRemove={handleTagSelect}
            selectedDates={filterState.dateFilter}
            onDateRemove={(date) => handleDateFilterChange(filterState.dateFilter.filter(d => d !== date))}
            selectedTimes={filterState.timeFilter}
            onTimeRemove={(time) => handleTimeFilterChange(filterState.timeFilter.filter(t => t !== time))}
            dateRangeStart={dateRangeStart}
            dateRangeEnd={dateRangeEnd}
            onDateRangeClear={() => { setDateRangeStart(null); setDateRangeEnd(null); }}
            searchText={filterState.searchText}
            onSearchTextClear={() => setFilterState(prev => ({ ...prev, searchText: '' }))}
            searchCity={filterState.searchCity}
            onSearchCityClear={() => setFilterState(prev => ({ ...prev, searchCity: '' }))}
            onClearAll={() => {
              setFilterState({
                selectedTags: [],
                searchText: '',
                searchCity: '',
                dateFilter: [],
                timeFilter: [],
                customDate: ''
              });
              setDateRangeStart(null);
              setDateRangeEnd(null);
              setMaxDistance(5);
            }}
          />
        )}
        {/* Mobile floating controls - Toggle left, Undo right - NOT rendered on desktop */}
        {!isDesktop && (
          <div className="home__mobile-controls">
            <BinaryToggle
              leftLabel="List"
              rightLabel="Map"
              value={viewMode === 'map'}
              onChange={(isMap) => setViewMode(isMap ? 'map' : 'list')}
            />
            {/* Undo button - visible only in list view */}
            {viewMode === 'list' && (
              <button
                className="home__undo-btn"
                onClick={() => undoLastRemoval()}
                disabled={Object.values(hiddenEventsDetails).filter((e: { reason: string }) => e.reason === 'dismiss').length === 0}
                aria-label="Undo last dismissed event"
              >
                <Icon name="RotateCcw" size={16} />
                <span>Undo</span>
              </button>
            )}
          </div>
        )}
        {/* 📅 Date Quick-Select Bar - fixed position (mobile only) */}
        {!isDesktop && (
          <DateQuickSelectBar
            selectedDates={resolvedPillDates}
            onDateToggle={handlePillToggle}
            onOpenFilters={handleOpenFiltersFromQuickSelect}
          />
        )}
        {/* Mobile: ActiveFiltersBar — pill dates (day_, today, tomorrow, weekend) hidden, custom_ dates shown as tags */}
        {/* Map view: below DatePicker | List view: below toggle */}
        {!isDesktop && (
          <div ref={activeFiltersRef} className="home__active-filters home__active-filters--below-datepicker">
            <ActiveFiltersBar
              selectedTags={filterState.selectedTags}
              onTagRemove={handleTagSelect}
              selectedDates={filterState.dateFilter.filter(d => d.startsWith('custom_') && !pillDateSet.has(d.substring(d.indexOf('_') + 1)))}
              onDateRemove={(date) => handleDateFilterChange(filterState.dateFilter.filter(d => d !== date))}
              selectedTimes={filterState.timeFilter}
              onTimeRemove={(time) => handleTimeFilterChange(filterState.timeFilter.filter(t => t !== time))}
              dateRangeStart={dateRangeStart}
              dateRangeEnd={dateRangeEnd}
              onDateRangeClear={() => { setDateRangeStart(null); setDateRangeEnd(null); }}
              searchText={filterState.searchText}
              onSearchTextClear={() => setFilterState(prev => ({ ...prev, searchText: '' }))}
              searchCity={filterState.searchCity}
              onSearchCityClear={() => setFilterState(prev => ({ ...prev, searchCity: '' }))}
              onClearAll={() => {
                setFilterState({
                  selectedTags: [],
                  searchText: '',
                  searchCity: '',
                  dateFilter: [],
                  timeFilter: [],
                  customDate: ''
                });
                setDateRangeStart(null);
                setDateRangeEnd(null);
                setMaxDistance(5);
              }}
            />
          </div>
        )}

        <div className="home__views">
          {viewMode === 'list' && (
            <div className="home__list-view home__list-view--active">
              {(eventsLoading || geoLoading) && listEvents.length === 0 && !hasEverLoadedEvents ? (
                <div className="home__skeleton-list">
                  <EventCardSkeleton />
                  <EventCardSkeleton />
                  <EventCardSkeleton />
                </div>
              ) : !eventsLoading && !geoLoading && listEvents.length === 0 ? (
                <EmptyStateMessage />
              ) : (
                renderEvents()
              )}
            </div>
          )}

          <div className={`home__map-view ${viewMode === 'map' ? 'home__map-view--active' : ''}`}>
            <MapErrorBoundary onSwitchToList={handleSwitchToList}>
              <MapLibreView
                events={mapEvents}
                onEventClick={handleMapEventClick}
                onLocationChange={handleCurrentLocationChange}
                initialLocation={effectiveLocation || undefined}
                onMapClick={handleMultiModalClose}
                isLoading={eventsLoading}
                onSwitchToList={handleSwitchToList}
              />
            </MapErrorBoundary>
          </div>
        </div>

        <BottomNav
          items={navigationItems}
          onBeforeDrawerOpen={handleDrawerOpen}
          forceFilterOpen={forceOpenFilter}
          onFilterClose={() => { setForceOpenFilter(false); }}
          filterProps={{
            viewMode,
            searchText: filterState.searchText,
            onSearchTextChange: (text: string) => setFilterState(prev => ({ ...prev, searchText: text })),
            searchCity: filterState.searchCity,
            setSearchCity: (city: string) => setFilterState(prev => ({ ...prev, searchCity: city })),
            currentLocation: effectiveLocation,
            onLocationChange: handleCurrentLocationChange,
            distance: maxDistance,
            onDistanceChange: setMaxDistance,
            selectedDates: filterState.dateFilter,
            onDateChange: handleDateFilterChange,
            selectedTimes: filterState.timeFilter,
            onTimeChange: handleTimeFilterChange,
            selectedTags: filterState.selectedTags,
            onTagSelect: handleTagSelect,
            dateRangeStart,
            dateRangeEnd,
            onDateRangeChange: handleDateRangeChange,
            onReset: () => {
              setFilterState({
                selectedTags: [],
                searchText: '',
                searchCity: '',
                dateFilter: [],
                timeFilter: [],
                customDate: ''
              });
              setDateRangeStart(null);
              setDateRangeEnd(null);
              setMaxDistance(5);
            }
          }}
        />
      </div>

      {/* 🔄 PHASE 4 PRD Navigation : Overlays conditionnels basés sur useModalStack */}
      {/* PreviewCardMap - Reste montée tant qu'elle est dans la stack */}
      {isPreviewCardMapOpen && previewCardMapData && (
        <PreviewCardMap
          events={previewCardMapData.events}
          location={previewCardMapData.location}
          isOpen={!isEventModalOpen} // Cachée si EventModal est au-dessus
          onClose={closeTopModal}
          onEventClick={(event) => {
            // Trouver les seriesEvents dans enrichedEvents si disponible
            const enrichedData = previewCardMapData.enrichedEvents?.find(
              e => e.main.id === event.id || e.main.seriesId === event.seriesId
            );
            openEventModal(event, {
              seriesEvents: enrichedData?.series as Event[] | undefined,
              fromPreviewCardMap: true,
              isMapView: true,
              userLocation: previewCardMapData.userLocation
            });
          }}
          enrichedEvents={previewCardMapData.enrichedEvents}
          isSingleEvent={previewCardMapData.events.length === 1}
        />
      )}

      {/* EventModal - Affichée quand elle est au top de la stack */}
      {isEventModalOpen && eventModalData && (
        <EventModal
          event={eventModalData.event}
          isOpen={true}
          onClose={closeTopModal}
          onFavorite={() => {}}
          userLocation={eventModalData.userLocation ? {
            latitude: eventModalData.userLocation.lat,
            longitude: eventModalData.userLocation.lng
          } : undefined}
          seriesEvents={eventModalData.seriesEvents}
        />
      )}

      {/* Fallback : EventModalRoute pour les liens partagés (accès direct à /event/:id) */}
      {!isEventModalOpen && !isPreviewCardMapOpen && (params.id || params.seriesId || window.location.pathname.startsWith('/events/')) && (
        <EventModalRoute />
      )}
      
      {/* Location fallback toast (non-blocking, shown when geoloc denied) */}
      <LocationFallbackToast />
    </div>
  );
};

export default HomePage;
